import { Stack } from "@braintrust/braintrust-ui-components";
import {
  Box,
  IconButton,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { CalendarIcon } from "@braintrust/braintrust-ui-components/Icons";
import { openLocationMismatchModal } from "@js/apps/admin/components/location-mismatch-modal";
import { useIsNodeStaff } from "@js/apps/common/hooks";
import { LocationWarning } from "@js/apps/freelancer/components/location-warning";
import { useBidInterviewIntendedMutation } from "@js/apps/jobs/apps/bids/api";
import { LimitedFreelancerLinks } from "@js/apps/jobs/apps/bids/components/limited-freelancer-links";
import { isPublicBidWithoutUser } from "@js/apps/jobs/apps/bids/helpers";
import { ApprovedBadge } from "@js/apps/jobs/components/approved-badge-with-tooltip";
import { handleBookingTalentsCalendarClick } from "@js/apps/jobs/utils";
import { RouterLink } from "@js/components/link";
import type { EmployerBidListBid, Job } from "@js/types/jobs";
import { pluralize } from "@js/utils";

import { BidCardLabels, BidItemStatusLabel } from "../components/components";
import {
  getBidRole,
  getBidUserLocation,
  getBidUserName,
  getRateLabel,
} from "../helpers";

import style from "./style.module.scss";

type UserDetailsProps = {
  bid: EmployerBidListBid;
  job: Job;
  isMobile: boolean;
};

export const UserDetails = ({ bid, job, isMobile }: UserDetailsProps) => {
  const isNodeStaff = useIsNodeStaff();
  const isLocationMismatched =
    bid.freelancer.location_mismatch || !bid.freelancer.location;
  const hasProfile = !isPublicBidWithoutUser(bid);
  const displayLocationMismatch =
    hasProfile && isLocationMismatched && isNodeStaff;

  const handleLocationWarningClick = () => {
    openLocationMismatchModal({ userId: bid.freelancer.id });
  };

  const applicantNameProps = hasProfile
    ? ({ to: bid.freelancer.link, target: "_blank", RouterLink } as const)
    : {};

  const bidRoleAndUserLocation = [getBidRole(bid), getBidUserLocation(bid)]
    .filter(Boolean)
    .join(" - ");

  return (
    <Stack sx={{ minWidth: 0, ml: 2, minHeight: "46px" }}>
      <Box sx={{ mt: -0.5 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Typography
            variant="label"
            size="medium"
            component="h3"
            ellipsis
            noTextDecoration
            sx={{ display: "block" }}
            {...applicantNameProps}
          >
            {getBidUserName(bid)}
          </Typography>
          <span>
            <ApprovedBadge
              isApproved={bid.freelancer.user.freelancer_approved}
            />
          </span>
        </Box>
        <Box sx={{ maxWidth: { lg: "300px" }, mt: "3px" }}>
          <Typography
            variant="paragraph"
            size="small"
            component="h4"
            sx={{ mt: -0.5 }}
          >
            {!!bidRoleAndUserLocation ? `${bidRoleAndUserLocation} ` : null}
            {displayLocationMismatch && (
              <LocationWarning
                onClick={handleLocationWarningClick}
                sx={{ display: "inline !important", button: { mt: "-3px" } }}
              />
            )}
          </Typography>
        </Box>
      </Box>
      {!isMobile && <BidCardLabels bid={bid} job={job} />}
    </Stack>
  );
};

type BidOverviewProps = UserDetailsProps;

export const BidOverview = ({ bid, job, isMobile }: BidOverviewProps) => {
  const [bidInterviewIntended] = useBidInterviewIntendedMutation();

  return (
    <Box className={style.freelancerOverview} data-testid="bid-overview">
      {isMobile && <BidCardLabels bid={bid} job={job} />}
      <Box className={style.freelancerDetails}>
        <BidItemStatusLabel bid={bid} />
        {!!bid.freelancer.years_experience && (
          <Typography component="h5" title="Experience">
            {bid.freelancer.years_experience} year
            {pluralize(bid.freelancer.years_experience)} in{" "}
            <Typography component="span" variant="label">
              {bid.freelancer.role?.name}
            </Typography>
          </Typography>
        )}
        <Typography component="h5">{getRateLabel(job, bid)}</Typography>
      </Box>
      <Box className={style.freelancerLinks}>
        <LimitedFreelancerLinks
          externalProfiles={bid.freelancer.external_profiles}
          IconButtonProps={{ variant: "white-border-beige" }}
        />
        {bid.calendar_url && (
          <IconButton
            onClick={async () =>
              await handleBookingTalentsCalendarClick(bid, bidInterviewIntended)
            }
            size="x-small"
            variant="white-border-beige"
            rel="noopener noreferrer"
            aria-label="Open calendar"
          >
            <CalendarIcon style={{ fontSize: "16px", margin: "0px 1px" }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};
